import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
    ApiCallError, getDepartureChecklists, saveDepartureChecklist, deleteDepartureChecklist,
    type ChecklistAdminPageDto, type ChecklistDto, type ChecklistRequirementDto,
} from '@/api/client';
import type { LookupsDto } from '@/api/types';

/**
 * DEPARTURE CHECKLISTS — what each client's loads are not finished without (#317).
 *
 * Pete, 2026-08-27: *"Can we make some kind of config screen by company, facility,
 * by client where we list their required documentation and steps and then show on
 * the appointments some how?"*
 *
 * ---- ⛔ THE LIST IS THE UNIT OF EDITING, NOT THE ROW -----------------------
 *
 * ⚠️ THAT FOLLOWS DIRECTLY FROM PETE'S *"different requirements"* FOR INBOUND. What
 * a person opens, changes and saves is "Hellman's outbound list" — one complete
 * statement. Saving rows one at a time would let a half-finished edit resolve
 * against a live load: three of the four requirements, with nothing saying so.
 *
 * ⛔ SO SAVE SENDS THE WHOLE LIST AND THE SERVER WRITES IT IN ONE TRANSACTION.
 * Either the new list is what a truck is judged against, or the old one is.
 *
 * ---- ⭐ THE VOCABULARY COMES FROM THE SERVER -------------------------------
 *
 * ⚠️ web/ and src/ are separate builds, so this screen cannot import the kinds. A
 * hand-typed copy would go stale the day a fifth is added — the screen would offer
 * four and the CHECK constraint would reject a row nobody could create. The GET
 * returns them, with their help text and whether each one actually does anything
 * yet (§1R.16).
 *
 * ---- ⛔ AND IT SAYS WHEN A CHOICE DOES NOTHING ----------------------------
 *
 * `standing` is configurable and NOT wired. ⚠️ Letting somebody build a requirement
 * that can never be satisfied, without saying so, is how a checklist quietly
 * becomes untrustworthy — and this one is meant to decide whether a truck leaves.
 */

interface Props {
    getToken: () => Promise<string>;
    lookups: LookupsDto;
}

const BLANK_REQ = (): ChecklistRequirementDto => ({
    requirementId: null,
    kind: 'document',
    label: '',
    documentType: '',
    copies: 1,
    /* ⛔ A NEW ROW DEFAULTS TO BLOCKING FOR A DOCUMENT AND ADVISORY FOR A STEP —
       see `onKindChange`. Documents are the ones Pete named; a step is far more
       often a reminder than a gate, and licenceCheck.ts already learned that a gate
       which fires every day gets cleared without being read. */
    isBlocking: true,
    sortOrder: 100,
    isActive: true,
});

const BLANK_LIST = (): ChecklistDto => ({
    checklistId: '',
    name: '',
    direction: 'OUTBOUND',
    companyCode: null,
    facilityCode: null,
    clientCode: null,
    carrierCode: null,
    priority: 100,
    notes: null,
    isActive: true,
    requirements: [BLANK_REQ()],
});

/** ⚠️ '' in a <select> means "any". Never sent as an empty string — see `orNull`. */
const orNull = (v: string): string | null => (v.trim().length === 0 ? null : v.trim());

export default function DepartureChecklistAdmin({ getToken, lookups }: Props) {
    const [page, setPage] = useState<ChecklistAdminPageDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<ChecklistDto | null>(null);
    const [removing, setRemoving] = useState<ChecklistDto | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setPage(await getDepartureChecklists(await getToken()));
            setError(null);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not read the checklists.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    const kinds = page?.kinds ?? [];
    const kindOf = (k: string) => kinds.find(x => x.kind === k);

    function onKindChange(i: number, kind: string) {
        setDraft(d => {
            if (!d) return d;
            const reqs = [...d.requirements];
            const isStep = kind === 'step';
            reqs[i] = {
                ...reqs[i],
                kind,
                /* ⛔ A STEP HAS NO DOCUMENT TYPE, and the server refuses one three
                   ways over. Clearing it here means the person never fills in a box
                   that is about to be rejected. */
                documentType: isStep ? null : reqs[i].documentType,
                /* ⚠️ COPIES IS MEANINGLESS ON A STEP — there is nothing to print —
                   so it is pinned rather than shown and ignored. */
                copies: isStep ? 1 : reqs[i].copies,
                /* ⭐ SWITCHING TO A STEP DROPS BLOCKING BY DEFAULT. The person can
                   put it back; the default is the one that does not put a scheduler
                   through an override every single day. */
                isBlocking: isStep ? false : reqs[i].isBlocking,
            };
            return { ...d, requirements: reqs };
        });
    }

    function move(i: number, delta: number) {
        setDraft(d => {
            if (!d) return d;
            const j = i + delta;
            if (j < 0 || j >= d.requirements.length) return d;
            const reqs = [...d.requirements];
            [reqs[i], reqs[j]] = [reqs[j], reqs[i]];
            /* ⭐ THE ORDER ON SCREEN IS THE ORDER SAVED AND THE ORDER PRINTED. The
               server renumbers sortOrder from the array position, so there is no
               second place where "which comes first" is decided. */
            return { ...d, requirements: reqs };
        });
    }

    async function save() {
        if (!draft) return;
        setBusy(true);
        setError(null);
        setSaved(null);
        try {
            await saveDepartureChecklist(await getToken(), {
                ...draft,
                checklistId: draft.checklistId || null,
                requirements: draft.requirements.map((r, i) => ({
                    ...r,
                    documentType: r.kind === 'step' ? null : orNull(r.documentType ?? ''),
                    sortOrder: (i + 1) * 10,
                })),
            });
            await load();
            setDraft(null);
            setSaved('Saved.');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save that list.');
        } finally {
            setBusy(false);
        }
    }

    async function runDelete() {
        if (!removing) return;
        setBusy(true);
        try {
            await deleteDepartureChecklist(await getToken(), removing.checklistId);
            await load();
            setRemoving(null);
            if (draft?.checklistId === removing.checklistId) setDraft(null);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not delete that list.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-semibold">Departure checklists</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                    What an appointment is not finished without. Scoped by company, facility, client and
                    carrier — <strong>the most specific list wins outright</strong>, and lists are
                    never combined. Inbound and outbound are separate lists, so editing one cannot
                    change the other.
                </p>
                {/**
                  * ⛔ THE DERIVE-DO-NOT-STORE RULE IS STATED ON THE SCREEN, because
                  * the first question anybody asks is "where do I tick these off?"
                  * and the answer is "you mostly do not".
                  */}
                <p className="mt-1 text-xs text-muted-foreground">
                    Document requirements are answered by the documents actually filed on the
                    load — there is nothing to tick, and nothing can go green while the paperwork
                    is missing. Only <em>steps</em> get a tick, because a step has no document to
                    speak for it.
                </p>
            </div>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                    {error}
                </div>
            )}
            {saved && !error && (
                <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    {saved}
                </div>
            )}

            {/* ---- the lists ------------------------------------------------ */}
            <div className="space-y-1">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">Configured lists</span>
                    {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </div>
                {(page?.lists ?? []).length === 0 && !loading && (
                    <div className="rounded border border-dashed px-3 py-2 text-xs text-muted-foreground">
                        None yet. Until a list exists, every appointment shows &ldquo;no departure
                        requirements are configured&rdquo; — which is deliberately not the same
                        message as &ldquo;everything is done&rdquo;.
                    </div>
                )}
                <div className="divide-y rounded border">
                    {(page?.lists ?? []).map(l => (
                        <button
                            key={l.checklistId}
                            type="button"
                            onClick={() => { setDraft({ ...l, requirements: [...l.requirements] }); setSaved(null); }}
                            className={'flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-muted/50 '
                                + (draft?.checklistId === l.checklistId ? 'bg-muted' : '')}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="font-medium">
                                    {l.name}
                                    {!l.isActive && <span className="ml-1 text-muted-foreground">(off)</span>}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {l.direction === 'INBOUND' ? 'Inbound' : 'Outbound'}
                                    {' · '}
                                    {[l.clientCode && `client ${l.clientCode}`,
                                      l.carrierCode && `carrier ${l.carrierCode}`,
                                      l.facilityCode && `facility ${l.facilityCode}`,
                                      l.companyCode && `company ${l.companyCode}`]
                                        .filter(Boolean).join(' + ') || 'everything'}
                                    {' · '}{l.requirements.length} requirement{l.requirements.length === 1 ? '' : 's'}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { setDraft(BLANK_LIST()); setSaved(null); }}
                >
                    <Plus className="mr-1 h-3.5 w-3.5" /> New list
                </Button>
            </div>

            {/* ---- the editor ----------------------------------------------- */}
            {draft && (
                <div className="space-y-3 rounded border p-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label htmlFor="dc-name">Name</Label>
                            <Input
                                id="dc-name"
                                value={draft.name}
                                maxLength={page?.maxLabel ?? 120}
                                placeholder="Hellman outbound"
                                onChange={e => setDraft({ ...draft, name: e.target.value })}
                                className="mt-1.5"
                            />
                        </div>

                        <div>
                            <Label htmlFor="dc-dir">Direction</Label>
                            {/**
                              * ⛔ NOT A FILTER — PART OF THE KEY. An inbound list and an
                              * outbound list for the same client are two independent
                              * lists. Changing this on an existing list re-points it at
                              * the other direction entirely, which is why it says so.
                              */}
                            <select
                                id="dc-dir"
                                value={draft.direction}
                                onChange={e => setDraft({ ...draft, direction: e.target.value as 'INBOUND' | 'OUTBOUND' })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="OUTBOUND">Outbound — requirements can hold the truck</option>
                                <option value="INBOUND">Inbound — requirements are advisory</option>
                            </select>
                        </div>

                        <div>
                            <Label htmlFor="dc-pri">Priority</Label>
                            <Input
                                id="dc-pri"
                                type="number"
                                min={0}
                                max={9999}
                                value={draft.priority}
                                onChange={e => setDraft({ ...draft, priority: Number(e.target.value) || 0 })}
                                className="mt-1.5"
                            />
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                Lower wins between lists of equal specificity.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="dc-client">Client</Label>
                            <select
                                id="dc-client"
                                value={draft.clientCode ?? ''}
                                onChange={e => setDraft({ ...draft, clientCode: orNull(e.target.value) })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="">Any client</option>
                                {lookups.clients.map(c => (
                                    <option key={c.ClientCode} value={c.ClientCode}>{c.ClientName}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label htmlFor="dc-carrier">Carrier</Label>
                            <select
                                id="dc-carrier"
                                value={draft.carrierCode ?? ''}
                                onChange={e => setDraft({ ...draft, carrierCode: orNull(e.target.value) })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="">Any carrier</option>
                                {lookups.carriers.map(c => (
                                    <option key={c.CarrierCode} value={c.CarrierCode}>{c.CarrierName}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                A client rule beats a carrier rule when both match.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="dc-fac">Facility</Label>
                            <select
                                id="dc-fac"
                                value={draft.facilityCode ?? ''}
                                onChange={e => setDraft({ ...draft, facilityCode: orNull(e.target.value) })}
                                className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                            >
                                <option value="">Any facility</option>
                                {lookups.facilities.map(f => (
                                    <option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-end gap-2 pb-1">
                            <label className="flex items-center gap-1.5 text-xs">
                                <input
                                    type="checkbox"
                                    checked={draft.isActive}
                                    onChange={e => setDraft({ ...draft, isActive: e.target.checked })}
                                />
                                Active
                            </label>
                        </div>
                    </div>

                    {/* ---- the requirements -------------------------------- */}
                    <div className="space-y-2">
                        <div className="text-xs font-medium">Requirements</div>
                        {draft.requirements.map((r, i) => {
                            const k = kindOf(r.kind);
                            const isStep = r.kind === 'step';
                            return (
                                <div key={i} className="space-y-1.5 rounded border bg-muted/30 p-2">
                                    <div className="flex items-start gap-2">
                                        <div className="flex shrink-0 flex-col gap-0.5 pt-1">
                                            <button
                                                type="button" title="Move up" aria-label="Move up"
                                                onClick={() => move(i, -1)} disabled={i === 0}
                                                className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-30"
                                            >
                                                <ArrowUp className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button" title="Move down" aria-label="Move down"
                                                onClick={() => move(i, 1)}
                                                disabled={i === draft.requirements.length - 1}
                                                className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-30"
                                            >
                                                <ArrowDown className="h-3 w-3" />
                                            </button>
                                        </div>

                                        <div className="min-w-0 flex-1 space-y-1.5">
                                            <Input
                                                value={r.label}
                                                maxLength={page?.maxLabel ?? 120}
                                                placeholder={isStep ? 'Check the seal number' : 'Packing slip'}
                                                onChange={e => {
                                                    const reqs = [...draft.requirements];
                                                    reqs[i] = { ...reqs[i], label: e.target.value };
                                                    setDraft({ ...draft, requirements: reqs });
                                                }}
                                            />
                                            <div className="flex flex-wrap items-center gap-2">
                                                <select
                                                    value={r.kind}
                                                    onChange={e => onKindChange(i, e.target.value)}
                                                    className="rounded border px-2 py-1 text-xs"
                                                >
                                                    {kinds.map(x => (
                                                        <option key={x.kind} value={x.kind}>
                                                            {x.label}{x.wired ? '' : ' (not wired yet)'}
                                                        </option>
                                                    ))}
                                                </select>

                                                {!isStep && (
                                                    <Input
                                                        value={r.documentType ?? ''}
                                                        placeholder="Document type (blank = any)"
                                                        maxLength={100}
                                                        onChange={e => {
                                                            const reqs = [...draft.requirements];
                                                            reqs[i] = { ...reqs[i], documentType: e.target.value };
                                                            setDraft({ ...draft, requirements: reqs });
                                                        }}
                                                        className="h-7 w-56 text-xs"
                                                    />
                                                )}

                                                {!isStep && (
                                                    <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                                        Print
                                                        <Input
                                                            type="number" min={1} max={page?.maxCopies ?? 20}
                                                            value={r.copies}
                                                            onChange={e => {
                                                                const reqs = [...draft.requirements];
                                                                reqs[i] = { ...reqs[i], copies: Number(e.target.value) || 1 };
                                                                setDraft({ ...draft, requirements: reqs });
                                                            }}
                                                            className="h-7 w-16 text-xs"
                                                        />
                                                    </label>
                                                )}

                                                {/**
                                                  * ⛔⛔ THE LABEL CHANGES ON INBOUND, AND IT IS
                                                  * NOT COSMETIC — IT STOPS THE CONTROL LYING.
                                                  *
                                                  * ⚠️ THERE IS NO INBOUND RELEASE GATE. #317 step 4
                                                  * wired unmet requirements into
                                                  * `OutboundReleaseDialog`, and that dialog stands
                                                  * between an OUTBOUND load and the gate because a
                                                  * fictitious pickup is the actual threat (design
                                                  * §3). Nothing equivalent guards a delivering
                                                  * truck — Pete, 2026-08-27: *"On inbounds, freight
                                                  * fraud is much less a concern."*
                                                  *
                                                  * ⛔ SO "HOLDS THE LOAD" ON AN INBOUND ROW WOULD BE
                                                  * A PROMISE THE SYSTEM DOES NOT KEEP. Somebody
                                                  * ticking it would reasonably expect a truck to be
                                                  * stopped, and none would be — the worst kind of
                                                  * config, because it fails silently and only in
                                                  * the case it was set up for.
                                                  *
                                                  * ⭐ THE VALUE IS STILL SAVED. It records intent,
                                                  * so an inbound gate built later inherits every
                                                  * decision already made rather than starting from
                                                  * a table of falses. What changes is only what the
                                                  * screen CLAIMS it does today.
                                                  */}
                                                <label className="flex items-center gap-1 text-[11px]">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.isBlocking}
                                                        disabled={k ? !k.wired : false}
                                                        onChange={e => {
                                                            const reqs = [...draft.requirements];
                                                            reqs[i] = { ...reqs[i], isBlocking: e.target.checked };
                                                            setDraft({ ...draft, requirements: reqs });
                                                        }}
                                                    />
                                                    {draft.direction === 'OUTBOUND'
                                                        ? 'Holds the truck'
                                                        : 'Call it out'}
                                                </label>

                                                <button
                                                    type="button"
                                                    title="Remove this requirement"
                                                    aria-label="Remove this requirement"
                                                    onClick={() => setDraft({
                                                        ...draft,
                                                        requirements: draft.requirements.filter((_, j) => j !== i),
                                                    })}
                                                    className="ml-auto rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>

                                            {k?.help && (
                                                <p className="text-[10px] text-muted-foreground">{k.help}</p>
                                            )}
                                            {/**
                                              * ⛔ SAID PLAINLY WHEN A CHOICE DOES NOTHING. A
                                              * requirement nothing can satisfy would otherwise
                                              * sit on every load looking like a gap, and the
                                              * server forces it advisory whatever this box says.
                                              */}
                                            {/**
                                              * ⚠️ SAID ONCE PER TICKED ROW, not once per screen. A
                                              * note at the top of a long form is a note nobody
                                              * reads at the moment they make the decision.
                                              */}
                                            {draft.direction === 'INBOUND' && r.isBlocking && (
                                                <p className="text-[10px] text-muted-foreground">
                                                    On an inbound load this makes the item stand out on the
                                                    appointment — it does not stop the truck. There is no
                                                    inbound release gate; the check at the gate guards
                                                    outbound appointments, where the freight is leaving.
                                                </p>
                                            )}
                                            {k && !k.wired && (
                                                <p className="flex items-start gap-1 text-[10px] text-amber-700">
                                                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                                                    This kind is not wired up yet. It will show on the appointment as a
                                                    note and cannot hold anything at the gate, whatever
                                                    &ldquo;Holds the appointment&rdquo; is set to.
                                                </p>
                                            )}
                                            {/**
                                              * ⚠️ COPIES IS EXPLAINED WHERE IT IS TYPED. Somebody
                                              * setting "2" is entitled to think that means the
                                              * checklist expects two filed documents. It does not,
                                              * and it must not: nothing here can watch a printer.
                                              */}
                                            {!isStep && r.copies > 1 && (
                                                <p className="text-[10px] text-muted-foreground">
                                                    &ldquo;Print {r.copies}&rdquo; is an instruction for the
                                                    print button. One filed document still satisfies this —
                                                    nothing here can see how many sheets came out.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <Button
                            type="button" variant="outline" size="sm"
                            disabled={draft.requirements.length >= (page?.maxRequirements ?? 40)}
                            onClick={() => setDraft({
                                ...draft, requirements: [...draft.requirements, BLANK_REQ()],
                            })}
                        >
                            <Plus className="mr-1 h-3.5 w-3.5" /> Add a requirement
                        </Button>
                    </div>

                    <div className="flex items-center gap-2 border-t pt-3">
                        <Button type="button" onClick={() => void save()} disabled={busy}>
                            {busy ? 'Saving…' : 'Save this list'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setDraft(null)} disabled={busy}>
                            Cancel
                        </Button>
                        {draft.checklistId && (
                            <Button
                                type="button" variant="outline" disabled={busy}
                                onClick={() => setRemoving(draft)}
                                className="ml-auto text-destructive"
                            >
                                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/**
              * ⛔ NEVER window.confirm. And the wording offers the reversible option
              * first, because "stop applying this" is almost always what somebody
              * means, and deleting takes the step ticks with it.
              */}
            <ConfirmDialog
                open={removing !== null}
                title="Delete this checklist?"
                message={
                    `"${removing?.name ?? ''}" and its requirements are removed for good, along `
                    + 'with every tick recorded against its steps.\n\n'
                    + 'If you only want to stop applying it, untick Active and save instead — '
                    + 'that keeps the list and its history.'
                }
                confirmLabel="Delete it"
                destructive
                busy={busy}
                onConfirm={() => void runDelete()}
                onCancel={() => setRemoving(null)}
            />
        </div>
    );
}
